import { useState, useMemo, useEffect } from 'react';
import { Search, AlertTriangle, Lock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  MOCK_TOOLS,
  TOOL_CATEGORIES,
  categoryIcons,
  categoryColors,
} from './mockTools';
import { AgentPermission, permissionKey } from './types';

export interface ToolsTabProps {
  enabledTools: Set<string>;
  permissions: AgentPermission[];
  onToolToggle: (
    toolId: string,
    enabled: boolean,
    requiredPermissions: AgentPermission[],
  ) => void;
}

export function ToolsTab({
  enabledTools,
  permissions,
  onToolToggle,
}: ToolsTabProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const permissionKeys = useMemo(
    () => new Set(permissions.map((p) => permissionKey(p.action, p.subject))),
    [permissions],
  );

  const { toolsByCategory, categories } = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const filtered = query
      ? MOCK_TOOLS.filter(
          (tool) =>
            tool.name.toLowerCase().includes(query) ||
            tool.description.toLowerCase().includes(query),
        )
      : MOCK_TOOLS;

    const grouped = new Map<string, typeof MOCK_TOOLS>();
    filtered.forEach((tool) => {
      if (!grouped.has(tool.category)) {
        grouped.set(tool.category, []);
      }
      grouped.get(tool.category)!.push(tool);
    });

    return {
      toolsByCategory: grouped,
      categories: TOOL_CATEGORIES.filter((cat) => grouped.has(cat)),
    };
  }, [searchQuery]);

  const categoriesWithEnabledTools = useMemo(() => {
    return TOOL_CATEGORIES.filter((category) =>
      MOCK_TOOLS.some(
        (tool) => tool.category === category && enabledTools.has(tool.id),
      ),
    );
  }, [enabledTools]);

  const [openCategories, setOpenCategories] = useState<string[]>(
    categoriesWithEnabledTools,
  );

  useEffect(() => {
    if (searchQuery.trim()) {
      setOpenCategories(categories);
    }
  }, [searchQuery, categories]);

  useEffect(() => {
    setOpenCategories((prev) => {
      const newCategories = categoriesWithEnabledTools.filter(
        (cat) => !prev.includes(cat),
      );
      return newCategories.length > 0 ? [...prev, ...newCategories] : prev;
    });
  }, [categoriesWithEnabledTools]);

  // Check if tool has missing permissions
  const hasMissingPermissions = (toolId: string) => {
    if (!enabledTools.has(toolId)) return false;

    const tool = MOCK_TOOLS.find((t) => t.id === toolId);
    if (!tool) return false;

    return !tool.requiredPermissions.every((rp) =>
      permissionKeys.has(permissionKey(rp.action, rp.subject)),
    );
  };

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search tools..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Tools by Category */}
      <Accordion
        type="multiple"
        value={openCategories}
        onValueChange={setOpenCategories}
        className="w-full"
      >
        {categories.map((category) => {
          const tools = toolsByCategory.get(category) || [];
          const enabledCount = tools.filter((t) =>
            enabledTools.has(t.id),
          ).length;

          return (
            <AccordionItem key={category} value={category}>
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2 flex-1">
                  <span className="text-muted-foreground">
                    {categoryIcons[category]}
                  </span>
                  <span className="font-medium">{category}</span>
                  {enabledCount > 0 && (
                    <Badge variant="secondary" className="ml-auto mr-2">
                      {enabledCount}
                    </Badge>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2 pt-2">
                  {tools.map((tool) => {
                    const isEnabled = enabledTools.has(tool.id);
                    const showWarning = hasMissingPermissions(tool.id);

                    return (
                      <div
                        key={tool.id}
                        className="flex items-start gap-3 p-3 rounded-md hover:bg-muted/50 transition-colors"
                      >
                        <Checkbox
                          id={tool.id}
                          checked={isEnabled}
                          onCheckedChange={(checked) => {
                            onToolToggle(
                              tool.id,
                              checked === true,
                              tool.requiredPermissions,
                            );
                          }}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <label
                              htmlFor={tool.id}
                              className="font-medium text-sm cursor-pointer"
                            >
                              {tool.name}
                            </label>
                            {showWarning && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Missing required permissions</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {tool.description}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      {/* Coming Soon Section */}
      <div className="mt-6 p-4 border border-dashed rounded-lg bg-muted/30">
        <div className="flex items-start gap-3">
          <Lock className="h-5 w-5 text-muted-foreground mt-0.5" />
          <div>
            <h3 className="font-medium text-sm text-muted-foreground">
              Custom Tools - Coming Soon
            </h3>
            <p className="text-sm text-muted-foreground/80 mt-1">
              Create your own tools to extend agent capabilities
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
